import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { ApiResponse, ApiError } from '../types/api';
import {
    ArcgisFieldValuesResult,
    ArcgisInspectResult,
    DataSource,
    DataSourceFormData,
    DataSourcePreview,
    SourceType,
} from '../types/dataSource';

interface BboxCoordinates {
    top: number;
    left: number;
    bottom: number;
    right: number;
}

export const useDataSources = (sourceType?: SourceType) => {
    const queryParams = sourceType ? `?source_type=${sourceType}` : '';

    const { data, isLoading, error } = useQuery({
        queryKey: ['dataSources', sourceType],
        queryFn: () => api.request<ApiResponse<DataSource[]>>(`/api/data_sources${queryParams}`),
        refetchOnWindowFocus: false,
    }) as { data: ApiResponse<DataSource[]>; isLoading: boolean; error: ApiError };

    return { dataSources: data?.data ?? [], isLoading, error };
};

export const useDataSourcesByBbox = (sourceType: SourceType, bbox?: BboxCoordinates) => {
    const params = new URLSearchParams({ source_type: sourceType });
    if (bbox) {
        params.append('top', String(bbox.top));
        params.append('left', String(bbox.left));
        params.append('bottom', String(bbox.bottom));
        params.append('right', String(bbox.right));
    }

    const { data, isLoading, error } = useQuery({
        queryKey: ['dataSources', sourceType, bbox],
        queryFn: () => api.request<ApiResponse<DataSource[]>>(`/api/data_sources?${params}`),
        refetchOnWindowFocus: false,
    }) as { data: ApiResponse<DataSource[]>; isLoading: boolean; error: ApiError };

    return { dataSources: data?.data ?? [], isLoading, error };
};

/** The parcels a property boundary source holds inside `bbox`, for the
 *  create wizard's map. The backend caps the area and feature count. */
export const fetchDataSourcePreview = async (id: number, bbox: BboxCoordinates) => {
    const params = new URLSearchParams({
        top: String(bbox.top),
        left: String(bbox.left),
        bottom: String(bbox.bottom),
        right: String(bbox.right),
    });
    const response = await api.get<ApiResponse<DataSourcePreview>>(
        `/api/data_sources/${id}/preview?${params}`
    );
    return response.data;
};

export const useDataSourceDetail = (id: number | null) => {
    const { data, isLoading } = useQuery({
        queryKey: ['dataSource', id],
        queryFn: () => api.request<ApiResponse<DataSource>>(`/api/data_sources/${id}`),
        enabled: id !== null,
        refetchOnWindowFocus: false,
    }) as { data: ApiResponse<DataSource>; isLoading: boolean };

    return { dataSource: data?.data ?? null, isLoading };
};

export const useCreateDataSource = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: DataSourceFormData) =>
            api.post<ApiResponse<DataSource>>('/api/data_sources', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dataSources'] });
        },
    });
};

export const useUpdateDataSource = (id: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Partial<DataSourceFormData>) =>
            api.put<ApiResponse<DataSource>>(`/api/data_sources/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dataSources'] });
        },
    });
};

export interface ArcgisInspectRequest {
    api_url?: string;
    credentials?: DataSourceFormData['credentials'];
    config?: Record<string, unknown>;
    /** Re-use the stored credentials of the source being edited when the
     *  user has not retyped them. */
    source_id?: number;
    /** `static` describes the layer for an init-time source (treatments,
     *  assets, ...) without the realtime polling checks; the default is the
     *  realtime probe. */
    mode?: 'static' | 'realtime';
}

/** "Test connection" for a customer ArcGIS realtime layer: lists the layers
 *  behind the URL with the fields the poller would use. Admin-only. */
export const inspectArcgisLayer = async (body: ArcgisInspectRequest) => {
    const response = await api.post<ApiResponse<ArcgisInspectResult>>(
        '/api/data_sources/arcgis/inspect',
        body
    );
    return response.data;
};

export interface ArcgisFieldValuesRequest {
    api_url?: string;
    credentials?: DataSourceFormData['credentials'];
    /** Re-use the stored credentials of the source being edited. */
    source_id?: number;
    /** The layer field whose distinct values are wanted. */
    field: string;
}

/** The distinct values a layer field holds, for mapping a treatment source's
 *  attribute values to vegetation types after "Test connection". Admin-only. */
export const fetchArcgisFieldValues = async (body: ArcgisFieldValuesRequest) => {
    const response = await api.post<ApiResponse<ArcgisFieldValuesResult>>(
        '/api/data_sources/arcgis/field_values',
        body
    );
    return response.data;
};

export const useDeleteDataSource = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.delete(`/api/data_sources/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dataSources'] });
        },
    });
};

export const useUploadShapefile = () => {
    return useMutation({
        mutationFn: (files: File[]) => {
            const formData = new FormData();
            files.forEach((f) => formData.append('files', f));
            return api.post<{ file_path: string }>('/api/data_sources/upload_shapefile', formData);
        },
    });
};
